import { useEffect, useRef } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import { EstadosOrdenTrabajoEnumChoice, ROUTER_PATHS, TipoEntidadOrdenServicioEnumChoice } from '@/shared';
import { useGetOrdenTrabajo } from '@/shared/actions/erp';
import { useCheckPermissions, useLoaders } from '@/shared/hooks';
import { AlertSeverityEnum } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { MaterialesKey, useMaterialesUtilizadosStore } from '@/store/erp';
import {
  SaveOrdenTrabajoAsignadaTec,
  SaveOrdenTrabajoVisitaTec,
} from '../../shared/components';
// Fix merge: el barrel exporta la versión de INSTALACIÓN (sin traslado). El
// componente con flujo de traslado se importa directo para enrutar traslados.
import SaveOrdenTrabajoTrasladoTec from '../../shared/components/SaveOrdenTrabajo/SaveOrdenTrabajoAsignadaTec';

export type InstalacionRecoordinadaTecnicoPageProps = {};

const InstalacionRecoordinadaTecnicoPage: React.FC<
  InstalacionRecoordinadaTecnicoPageProps
> = () => {
  useCheckPermissions(PermissionsEnum.api_change_ordentrabajo);
  const returnUrlInstalacionesTecnicoPage =
    ROUTER_PATHS.tecnico.ordenenTrabajoNav;
  const navigate = useNavigate();

  const clearSelectedItems = useMaterialesUtilizadosStore(s => s.clearSelectedItems);
  const setAlert = useUiStore.getState().setAlert;

  const { id } = useParams();
  const { data, isLoading } = useGetOrdenTrabajo(id!);
  useLoaders(isLoading);

  // Validar UNA sola vez por OT id. Sin el ref, un PUT del form (que cambia
  // estado a REALIZADA) dispararia el alert otra vez al refetch automatico.
  const validatedRef = useRef<string | null>(null);

  useEffect(() => {
    // Limpiar el store global de materiales al entrar a una OT distinta:
    // si el usuario venia de otro registro (Asignadas, Recoordinadas, etc.)
    // los items seleccionados de ese contexto contaminaban el form actual.
    clearSelectedItems(MaterialesKey.equipos_utilizados);
    clearSelectedItems(MaterialesKey.materiales_utilizados);

    const otId = data?.data?.id_orden_trabajo;
    const estado = data?.data?.estado_orden_trabajo;
    if (!otId || !estado) return;
    if (validatedRef.current === otId) return;
    validatedRef.current = otId;

    if (estado !== EstadosOrdenTrabajoEnumChoice.ESPERA_RECOORDINADA) {
      setAlert(
        true,
        'La orden de trabajo no esta en estado de recoordinada',
        AlertSeverityEnum.error
      );
      navigate(returnUrlInstalacionesTecnicoPage);
    }
  }, [data?.data?.id_orden_trabajo, data?.data?.estado_orden_trabajo]);

  // Mismo patron que InstalacionAsignadaTecnicoPage: sin isRefetching en el
  // early return — el invalidateQuery del form no debe desmontar la pantalla.
  if (isLoading) return null;
  if (!data?.data?.id_orden_trabajo)
    return <Navigate to={returnUrlInstalacionesTecnicoPage} />;

  const tipoServ = data?.data?.agendamiento_data?.tipo_orden_servicio;
  const esVisita = tipoServ === TipoEntidadOrdenServicioEnumChoice.VISITA;
  const esTraslado =
    tipoServ === 'TRASLADO' || (data?.data as any)?.tipo_orden_trabajo === 'TRASLADO';

  if (esVisita) {
    return (
      <SaveOrdenTrabajoVisitaTec
        title="Orden de Trabajo"
        ordentrabajo={data?.data}
      />
    );
  }

  if (esTraslado) {
    return (
      <SaveOrdenTrabajoTrasladoTec
        title="Orden de Trabajo"
        ordentrabajo={data?.data}
      />
    );
  }

  return (
    <SaveOrdenTrabajoAsignadaTec
      title="Orden de Trabajo"
      ordentrabajo={data?.data}
    />
  );
};

export default InstalacionRecoordinadaTecnicoPage;
